import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
} from '@mui/material'

import TextInputController from '@/components/ui/inputs/TextInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import useConsultationValidationFormSchema, { ConsultationValidationFormValues } from '../hooks/useConsultationValidationFormSchema'
import { PaymentType } from '@/core/models/consultation.model'

type Props = {
    open: boolean
    onClose: () => void
    onSubmit: (values: ConsultationValidationFormValues) => void
    selectedCount: number
}

const paymentTypeOptions = Object.values(PaymentType).map((type) => ({ value: type, label: type }))

export default function ConsultationValidationModal({ open, onClose, onSubmit, selectedCount }: Props) {
    const { t } = useTranslation()
    const schema = useConsultationValidationFormSchema()

    const methods = useForm<ConsultationValidationFormValues>({
        defaultValues: {
            price: 0,
            paymentType: PaymentType.Card,
        },
        resolver: yupResolver(schema),
    })

    const handleSubmit = (values: ConsultationValidationFormValues) => {
        onSubmit(values)
        onClose()
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t('Validate Consultations')} ({selectedCount})
            </DialogTitle>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <DialogContent>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextInputController
                                    control={methods.control}
                                    name="price"
                                    label={t('field.price.label')}
                                    type="number"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <SelectInputController
                                    control={methods.control}
                                    name="paymentType"
                                    label={t('field.paymentType.label')}
                                    options={paymentTypeOptions}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>
                            {t('common:button.cancel.label')}
                        </Button>
                        <Button type="submit" variant="contained" color="primary">
                            {t('common:button.validate.label')}
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    )
} 